/**
 * RelatedPersonCard — uma pessoa relacionada do Bond (vínculo factual, ADR-129).
 *
 * Mostra pessoa, tipo de relacionamento, status, período (início/fim), fonte e
 * versão. Para vínculos ATIVOS oferece dois fluxos inline (espelham o padrão de
 * edição/revogação do B1 Contatos Autorizados):
 *   - Editar tipo → RelationshipTypePicker, PATCH com If-Match version.
 *   - Encerrar → motivo + data OPCIONAIS, DELETE (terminate) com If-Match version.
 *
 * Cada mutação reporta erro inline (inclui conflito de versão 412 do api-client).
 */

import { useState } from 'react';

import {
  useTerminateRelatedPerson,
  useUpdateRelatedPerson,
} from '../../../hooks/use-bond-related-persons';

import {
  buildTerminateRequest,
  buildUpdateRequest,
  editFormFrom,
  initialTerminateForm,
  validateEditForm,
  type RelatedPersonFormState,
  type TerminateFormState,
} from './related-persons-form';
import { formatDate, relationshipLabel, sourceLabel, statusLabel } from './related-persons-labels';
import { RelationshipTypePicker } from './RelationshipTypePicker';

import type { BondRelatedPersonWireItem } from '../../../lib/api-client';

type Mode = 'view' | 'edit' | 'terminate';

export function RelatedPersonCard({
  bondId,
  person,
}: {
  bondId: string;
  person: BondRelatedPersonWireItem;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('view');
  const update = useUpdateRelatedPerson(bondId);
  const terminate = useTerminateRelatedPerson(bondId);

  const [form, setForm] = useState<RelatedPersonFormState>(() => editFormFrom(person));
  const [terminateForm, setTerminateForm] = useState<TerminateFormState>(initialTerminateForm);
  const [error, setError] = useState<string | null>(null);

  const isActive = person.status === 'active';

  function resetTo(next: Mode): void {
    setError(null);
    setForm(editFormFrom(person));
    setTerminateForm(initialTerminateForm());
    setMode(next);
  }

  function handleSaveEdit(): void {
    const v = validateEditForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    update.mutate(
      { relatedPersonId: person.id, version: person.version, body: buildUpdateRequest(form) },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao salvar.'),
      },
    );
  }

  function handleConfirmTerminate(): void {
    setError(null);
    terminate.mutate(
      {
        relatedPersonId: person.id,
        version: person.version,
        body: buildTerminateRequest(terminateForm),
      },
      {
        onSuccess: () => setMode('view'),
        onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao encerrar.'),
      },
    );
  }

  const terminationReason =
    typeof person.metadata.terminationReason === 'string'
      ? person.metadata.terminationReason
      : null;

  return (
    <div className={`brp-card brp-card--${person.status}`}>
      <div className="brp-card__head">
        <span className="brp-card__person" title={person.personId}>
          {person.personId}
        </span>
        <span className={`brp-status brp-status--${person.status}`}>
          {statusLabel(person.status)}
        </span>
      </div>

      <div className="brp-card__type">
        <span className="brp-tag">{relationshipLabel(person.relationshipType)}</span>
      </div>

      <dl className="brp-card__meta">
        <div>
          <dt>Início</dt>
          <dd>{formatDate(person.startedAt)}</dd>
        </div>
        <div>
          <dt>Fonte</dt>
          <dd>{sourceLabel(person.source)}</dd>
        </div>
        {person.status === 'terminated' && (
          <div>
            <dt>Encerrada</dt>
            <dd>
              {formatDate(person.endedAt)}
              {terminationReason !== null ? ` · ${terminationReason}` : ''}
            </dd>
          </div>
        )}
      </dl>

      {/* Ações (apenas vínculos ativos) */}
      {isActive && mode === 'view' && (
        <div className="brp-card__actions">
          <button type="button" className="brp-btn brp-btn--ghost" onClick={() => resetTo('edit')}>
            Editar tipo
          </button>
          <button
            type="button"
            className="brp-btn brp-btn--danger"
            onClick={() => resetTo('terminate')}
          >
            Encerrar
          </button>
        </div>
      )}

      {/* Editar inline */}
      {mode === 'edit' && (
        <div className="brp-inline">
          <label className="brp-field" htmlFor={`edit-type-${person.id}`}>
            <span className="brp-field__label">Tipo de relacionamento</span>
            <RelationshipTypePicker
              id={`edit-type-${person.id}`}
              value={form.relationshipType}
              onChange={(t) => setForm((p) => ({ ...p, relationshipType: t }))}
            />
          </label>
          {error !== null && (
            <p className="brp-alert brp-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="brp-inline__actions">
            <button
              type="button"
              className="brp-btn brp-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="brp-btn brp-btn--primary"
              disabled={update.isPending}
              onClick={handleSaveEdit}
            >
              {update.isPending ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      )}

      {/* Encerrar inline */}
      {mode === 'terminate' && (
        <div className="brp-inline">
          <label className="brp-field">
            <span className="brp-field__label">Data de encerramento (opcional)</span>
            <input
              type="date"
              className="brp-input"
              value={terminateForm.endedAt}
              onChange={(e) => setTerminateForm((p) => ({ ...p, endedAt: e.target.value }))}
            />
          </label>
          <label className="brp-field">
            <span className="brp-field__label">Motivo (opcional)</span>
            <textarea
              className="brp-textarea"
              rows={2}
              value={terminateForm.reason}
              onChange={(e) => setTerminateForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder="ex: separação / desligamento — opcional"
            />
          </label>
          {error !== null && (
            <p className="brp-alert brp-alert--error" role="alert">
              {error}
            </p>
          )}
          <div className="brp-inline__actions">
            <button
              type="button"
              className="brp-btn brp-btn--ghost"
              onClick={() => setMode('view')}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="brp-btn brp-btn--danger"
              disabled={terminate.isPending}
              onClick={handleConfirmTerminate}
            >
              {terminate.isPending ? 'Encerrando…' : 'Confirmar encerramento'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
